import React, {useCallback, useEffect, useState, useMemo} from "react";
import Map, {Layer, MapMouseEvent, MapRef, Source} from "react-map-gl";
import dayjs from "dayjs";
import {useAtom, useAtomValue} from "jotai";
import {FeatureCollection, MultiPolygon, Polygon, GeoJsonProperties} from "geojson";
import {Box, Button, Stack} from "@mui/material";
import BackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import CenterLocationIcon from "@mui/icons-material/GpsNotFixed";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {bboxToMapbox, fieldInsideMap} from "../../utils/mapUtils";
import {FieldMapRasterLayer, FieldMapAggregatedLayer, FieldMapLayer} from "../../types/fieldMapLayer";
import {mapTokenLoadable} from "../../store/mapTokenStore";
import {activeCompanyResultsSignedUrlLoadable} from "../../store/activeCompanyResultsImagesStore";
import {MapLegend} from "./MapLegend";
import {MapLayerSelector} from "./MapLayerSelector";
import maderaFarmUnits from "../../assets/madera/madera2023.json";
import {MapSelectedLayers} from "../../types/mapSelectedLayers";
import {FieldResultLayersProps} from "../../types/domain/fieldResult";
import {useTranslation} from "react-i18next";
import {MapSlider} from "./MapSlider";
import {ResultsDatePicker} from "../base/ResultsDatePicker";
import {activeCompanyFieldsLoadable} from "../../store/activeCompanyFieldsStore";
import {useFieldPopup} from "../../hooks/fieldPopupHooks";
import {MapPopup} from "./MapPopup";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {useNavigate, useParams} from "react-router-dom";
import {
    AGGREGATED_LAYERS,
    AGGREGATED_LAYERS_MAP,
    COMMON_RASTER_LAYERS,
    createLayersMapFromLayers,
    EMPTY_RASTER_LAYER,
    isAggregatedLayerType,
    isRasterLayerType,
    LEAF_TEMP_RASTER_LAYER,
    MADERA_RASTER_LAYERS,
    RasterLayerProps,
    SUGARCANE_RASTER_LAYERS,
} from "../../utils/layersUtils";
import {RasterLayerType} from "../../types/domain/rasterLayerType";
import {AggregatedLayerType} from "../../types/domain/aggregatedLayerType";
import {FillLayerSpecification, LineLayerSpecification} from "mapbox-gl";
import {ScoutingNote} from "../../types/scoutingNote";
import {activeMapFeatureAtom} from "../../store/activeMapFeatureStore";
import {PNG_HOST} from "../../utils/constants";
import {mapRefAtom} from "../../store/mapSelectedLayersStore";
import {isMaderaCompany} from "../../utils/maderaUtils.ts";
import {useCompanyFeatureFlag} from "../../hooks/featureFlagsHook.ts";
import {FeatureFlagEnum} from "../../types/featureFlagsEnum.ts";
import {useCompany} from "../../hooks/companiesHooks.ts";

const farmUnits: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: "FeatureCollection",
    features: maderaFarmUnits as unknown as GeoJSON.FeatureCollection<GeoJSON.Geometry>["features"],
};

export interface Props {
    geojson: FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> | null;
    onSplitTrigger: () => void;
    splitControl: "openSplit" | "closeSplit" | "none";
    onMapRefChange: (map: MapRef) => void;
    alignLegend: "left" | "right";
    mapSelectedLayers: MapSelectedLayers;
    setMapSelectedLayers: (value: MapSelectedLayers) => void;
    date: dayjs.Dayjs | null;
    setDate?: (date: dayjs.Dayjs | null) => void;
    showDateSelector: boolean;
    layerResults: Record<string, FieldResultLayersProps> | null;
    selectedFieldsIds: string[] | null;
    enableMaderaFarmUnitsLayer: boolean;
    showRealign: boolean;
    showSlider: boolean;
    onRealign: () => void;
    scoutingNotes?: ScoutingNote[];
    images?: Record<string, string>;
}

export const BaseMap: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {companyId} = useParams();
    const {
        geojson,
        onSplitTrigger,
        splitControl,
        onMapRefChange,
        alignLegend,
        mapSelectedLayers,
        setMapSelectedLayers,
        date,
        setDate,
        showDateSelector,
        layerResults,
        selectedFieldsIds,
        enableMaderaFarmUnitsLayer,
        showRealign,
        showSlider,
        onRealign,
        scoutingNotes,
        images,
    } = props;
    const [zoom, setZoom] = useState<number>(0);
    const [visibleFieldsHash, setVisibleFieldsHash] = useState<string>("");
    const [visibleFields, setVisibleFields] = useState<string[]>([]);
    const mapToken = useAtomValue(mapTokenLoadable);
    const [mapRef, setMapRef] = useAtom(mapRefAtom);
    const [layers, setLayers] = useState<FieldMapLayer[]>([]);
    const [scoutingNotesVisible, setScoutingNotesVisible] = useState(false);
    const [, setActiveMapFeature] = useAtom(activeMapFeatureAtom);

    const [isBordersLayerCreated, setIsBordersLayerCreated] = useState<boolean>();

    const company = useCompany();
    const isMadera = company?.data && isMaderaCompany(company.data);

    const hasLeafTemperatureLayer = useCompanyFeatureFlag(FeatureFlagEnum.leafTemperature);
    const hasSugarCaneLayers = useCompanyFeatureFlag(FeatureFlagEnum.sugarCaneLayer);

    let availableRasterLayers: RasterLayerProps[];
    if (isMadera) {
        availableRasterLayers = MADERA_RASTER_LAYERS(t);
    } else {
        availableRasterLayers = COMMON_RASTER_LAYERS(t);
        if (hasLeafTemperatureLayer) {
            availableRasterLayers.push(LEAF_TEMP_RASTER_LAYER);
        }
        if (hasSugarCaneLayers) {
            availableRasterLayers.push(...SUGARCANE_RASTER_LAYERS);
        }
        availableRasterLayers.push(EMPTY_RASTER_LAYER);
    }

    const AVAILABLE_RASTER_LAYERS_MAP = createLayersMapFromLayers(availableRasterLayers);
    let chosenLayerProps;
    if (isRasterLayerType(mapSelectedLayers.layerType)) {
        chosenLayerProps = AVAILABLE_RASTER_LAYERS_MAP[mapSelectedLayers.chosenLayer as RasterLayerType];
    } else {
        chosenLayerProps = AGGREGATED_LAYERS_MAP(t)[mapSelectedLayers.chosenLayer as AggregatedLayerType];
    }

    useEffect(() => {
        if (!mapRef) return;

        const checkBordersLayer = () => {
            if (!isBordersLayerCreated) {
                const bordersLayerFound = mapRef.getStyle()?.layers?.some((layer) => layer.id === "borders-layer");
                if (bordersLayerFound) setIsBordersLayerCreated(true);
            }
        };
        mapRef.on("styledata", checkBordersLayer);

        return () => {
            mapRef.off("styledata", checkBordersLayer);
        };
    }, [mapRef, isBordersLayerCreated]);

    const handleMapRef = useCallback(
        (ref: MapRef) => {
            if (ref) setMapRef(ref);
            onMapRefChange(ref);
        },
        [setMapRef, onMapRefChange],
    );

    const activeResultsSignedUrl = useAtomValue(activeCompanyResultsSignedUrlLoadable);
    const fields = useLoadableValueOrDefault(activeCompanyFieldsLoadable, [], (atom) => atom.fields);

    const geojsonFieldIds = useMemo(() => {
        if (!geojson) return new Set<string>();
        const fieldIds = geojson.features.map((f) => f.properties?.id as string).filter((id) => id !== undefined);
        return new Set(fieldIds);
    }, [geojson]);

    const {onHover, onLeave, popupDetails} = useFieldPopup(fields, "popup-fill-layer", date);

    const handleMapMoveEnd = () => {
        if (mapRef && mapRef?.getZoom() > 11) {
            const mapBounds = mapRef?.getBounds();

            const visibleFieldIds = fields
                .filter((f) => {
                    if (f.geometry && mapBounds) {
                        return geojsonFieldIds.has(f.id) && fieldInsideMap(f.geometry, mapBounds);
                    }
                    return false;
                })
                .map((f) => f.id);

            const newHash = visibleFieldIds.sort().join(",");
            if (visibleFieldsHash !== newHash) {
                setVisibleFieldsHash(newHash);
                setVisibleFields(visibleFieldIds);
            }
        } else {
            setVisibleFields([]);
            setVisibleFieldsHash("");
        }
    };
    const handleMapClick = (e: MapMouseEvent) => {
        const foundField = e.features?.find((feature) => {
            return feature.source === "fields";
        });

        if (foundField) {
            const fieldId = foundField.properties!.id as string;
            const path = `/map/${companyId}/field/${fieldId}`;
            navigate({
                pathname: path,
                search: location.search,
            });
        }

        const foundScoutingNote = e.features?.find((feature) => {
            return feature.source === "scouting-notes";
        });

        const activeScoutingNote =
            scoutingNotes?.find((scoutingNote) => scoutingNote.id === foundScoutingNote?.properties?.id) ?? null;
        setActiveMapFeature(() => activeScoutingNote);
    };
    const handleMouseLeave = () => {
        if (mapRef?.getCanvas()) {
            mapRef.getCanvas().style.cursor = "";
        }
        onLeave();
    };

    const visibleGeojson = useMemo(() => {
        if (!geojson) return null;

        return {
            ...geojson,
            features: geojson.features.filter((feature) => {
                return visibleFields.includes(feature.properties?.id as string);
            }),
        };
    }, [geojson, visibleFields]);

    useEffect(() => {
        if (!scoutingNotesVisible) {
            setActiveMapFeature(null);
        }
    }, [scoutingNotesVisible, setActiveMapFeature]);

    useEffect(() => {
        if (!mapRef || !images) return;

        Object.keys(images).forEach((imageKey) => {
            mapRef.loadImage(images[imageKey], (error, image) => {
                if (error) {
                    console.error("Error loading image:", error);
                    return;
                }
                if (image && !mapRef.hasImage(imageKey)) {
                    mapRef.addImage(imageKey, image);
                }
            });
        });
    }, [mapRef, images]);

    const handleMouseEnter = () => {
        if (mapRef?.getCanvas()) {
            mapRef.getCanvas().style.cursor = "pointer";
        }
    };

    function toggleScoutingNotesVisible() {
        setScoutingNotesVisible(!scoutingNotesVisible);
    }

    useEffect(() => {
        const {chosenLayer, layerType} = mapSelectedLayers;
        if (
            !layerResults ||
            !selectedFieldsIds ||
            !date ||
            chosenLayer === RasterLayerType.NONE ||
            activeResultsSignedUrl.state !== "hasData"
        ) {
            setLayers([]);
            return;
        }
        const dateStr = date.format("YYYYMMDD");
        const isAggregated = isAggregatedLayerType(layerType);
        const isRaster = isRasterLayerType(layerType);
        const layerConfig = isAggregated ? AGGREGATED_LAYERS().find((layer) => layer.type === chosenLayer) : null;
        const newLayers = Object.entries(layerResults)
            .filter(([, resultProps]) => {
                if (isAggregated) {
                    return resultProps?.aggregatedProps?.[chosenLayer as AggregatedLayerType] !== undefined;
                } else if (isRaster) {
                    return resultProps?.rasterProps?.[chosenLayer as RasterLayerType];
                }
                return false;
            })
            .filter(([id]) => visibleFields.includes(id))
            .map(([fieldId, resultProps]: [string, FieldResultLayersProps]) => {
                if (isAggregated && layerConfig?.getColor) {
                    const aggregatedLayerData = resultProps.aggregatedProps[chosenLayer as AggregatedLayerType];
                    const field = fields.find((f) => f.id === fieldId);
                    if (!field?.geometry) {
                        return null;
                    }
                    const color = layerConfig.getColor(aggregatedLayerData, field.irrigationTypeId === "10");
                    return {
                        fieldId,
                        geometry: field.geometry,
                        color,
                        date: dateStr,
                        isSelected: selectedFieldsIds.includes(fieldId),
                        chosenLayer: chosenLayer,
                        layerType: layerType,
                    } as FieldMapAggregatedLayer;
                } else if (isRaster) {
                    const rasterLayerProps = resultProps.rasterProps[chosenLayer as RasterLayerType];
                    const {bbox, imageId, min, max} = rasterLayerProps;

                    const signature = activeResultsSignedUrl.data;
                    const imageFileName = imageId.startsWith(chosenLayer)
                        ? `${imageId}.png`
                        : `${chosenLayer}_${imageId}.png`;
                    const url = `${PNG_HOST}/pngs/${fieldId}/${dateStr}/${imageFileName}${signature}`;
                    return {
                        fieldId,
                        url,
                        coordinates: bboxToMapbox(bbox),
                        date: dateStr,
                        chosenLayer: chosenLayer,
                        layerType: layerType,
                        min,
                        max,
                        isSelected: selectedFieldsIds.includes(fieldId),
                    } as FieldMapRasterLayer;
                } else {
                    return null;
                }
            })
            .filter((layer): layer is FieldMapLayer => layer !== null);

        setLayers(newLayers);
    }, [
        layerResults,
        setLayers,
        activeResultsSignedUrl,
        mapSelectedLayers,
        date,
        selectedFieldsIds,
        visibleFields,
        fields,
    ]);

    const aggregatedLayers = useMemo(() => {
        return layers.filter((l): l is FieldMapAggregatedLayer => isAggregatedLayerType(l.layerType));
    }, [layers]);

    const getGeoJsonSource = useCallback(() => {
        const prefix = "aggregated-layer";

        const data: FeatureCollection = {
            type: "FeatureCollection",
            features: aggregatedLayers.map((l) => ({
                type: "Feature",
                geometry: l.geometry,
                properties: {id: l.fieldId, color: l.color},
            })),
        };

        return (
            <Source key={`${prefix}-geojson`} id={`${prefix}-geojson`} type="geojson" data={data}>
                <Layer
                    id={`${prefix}-layer`}
                    type="fill"
                    source={`${prefix}-source`}
                    paint={{
                        "fill-color": ["get", "color"],
                        "fill-opacity": 1,
                    }}
                    beforeId="borders-layer"
                />
            </Source>
        );
    }, [aggregatedLayers]);

    const rasterLayers = useMemo(() => {
        return layers.filter((l): l is FieldMapRasterLayer => isRasterLayerType(l.layerType));
    }, [layers]);

    const getRasterLayersSources = useCallback(() => {
        return rasterLayers.map((rasterLayer) => {
            const prefix = `${rasterLayer.fieldId}-${rasterLayer.date}-raster`;

            return (
                <Source
                    key={prefix}
                    id={`${prefix}-source`}
                    type="image"
                    url={rasterLayer.url}
                    coordinates={rasterLayer.coordinates}
                >
                    <Layer
                        id={`${prefix}-layer`}
                        type="raster"
                        source={`${prefix}-source`}
                        paint={{
                            "raster-opacity": 1,
                            "raster-resampling": "nearest",
                            ...(!rasterLayer.isSelected && {
                                "raster-saturation": -0.35,
                                "raster-brightness-max": 0.75,
                                "raster-opacity": 0.9,
                            }),
                        }}
                        beforeId="borders-layer"
                    />
                </Source>
            );
        });
    }, [rasterLayers]);

    return (
        <Box sx={{height: "100%", width: "100%", position: "relative"}}>
            {mapToken.state === "hasData" && (
                <Map
                    reuseMaps
                    ref={handleMapRef}
                    mapStyle={getMapStyle(mapSelectedLayers)}
                    mapboxAccessToken={mapToken.data}
                    onZoom={(e) => setZoom(e.viewState.zoom)}
                    interactiveLayerIds={["popup-fill-layer", "scouting-notes"]}
                    //we use onMouseMove instead of onMouseEnter, because onMouseEnter is not triggered if you move
                    //from one feature to another, if they are adjacent(without leaving the layer).
                    onMouseMove={onHover}
                    onMouseLeave={handleMouseLeave}
                    onMouseEnter={handleMouseEnter}
                    onMoveEnd={handleMapMoveEnd}
                    onLoad={handleMapMoveEnd}
                    onClick={handleMapClick}
                >
                    {isBordersLayerCreated && getGeoJsonSource()}
                    {isBordersLayerCreated && getRasterLayersSources()}

                    {enableMaderaFarmUnitsLayer && isBordersLayerCreated && (
                        <Source id="madera" type="geojson" data={farmUnits}>
                            <Layer
                                {...MADERA_FILL_LAYER_STYLE}
                                beforeId="borders-layer"
                                layout={{visibility: mapSelectedLayers.madera ? "visible" : "none"}}
                            />
                            <Layer
                                {...MADERA_LINE_LAYER_STYLE}
                                beforeId="borders-layer"
                                layout={{visibility: mapSelectedLayers.madera ? "visible" : "none"}}
                            />
                        </Source>
                    )}

                    {geojson !== null && (
                        <Source id="fields" type="geojson" data={visibleGeojson}>
                            <Layer {...getBorderLayerStyle(selectedFieldsIds)} />
                            <Layer {...FILL_LAYER_STYLE} />
                        </Source>
                    )}
                    {popupDetails && <MapPopup details={popupDetails} t={t} />}

                    {scoutingNotes?.length && scoutingNotesVisible && (
                        <Source
                            id="scouting-notes"
                            type="geojson"
                            data={{
                                type: "FeatureCollection",
                                features: scoutingNotes?.map((note) => ({
                                    type: "Feature",
                                    geometry: {
                                        type: "Point",
                                        coordinates: note.location.coordinates,
                                    },
                                    properties: note,
                                })),
                            }}
                        >
                            <Layer
                                id="scouting-notes"
                                type="symbol"
                                layout={{
                                    "icon-image": "scoutingnote",
                                    "icon-size": 1,
                                    "icon-allow-overlap": true,
                                }}
                            />
                        </Source>
                    )}
                </Map>
            )}

            <Box sx={{position: "absolute", top: 0, left: 0, marginTop: "1rem", marginLeft: "1rem"}}>
                <Stack direction="column" gap="1rem">
                    {showRealign && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={onRealign}
                            startIcon={<CenterLocationIcon />}
                            sx={{
                                "& .MuiButton-startIcon": {margin: 0},
                                minWidth: 0,
                                padding: "0.5rem",
                                width: "2rem",
                                height: "2rem",
                            }}
                        />
                    )}
                    {showSlider && <MapSlider zoom={zoom} onZoomChange={(value) => mapRef?.setZoom(value)} />}
                    <Button
                        variant="contained"
                        color={scoutingNotesVisible ? "primary" : "secondary"}
                        onClick={toggleScoutingNotesVisible}
                        startIcon={<ChatBubbleOutlineIcon />}
                        sx={{
                            "& .MuiButton-startIcon": {margin: 0},
                            minWidth: 0,
                            padding: "0.5rem",
                            width: "2rem",
                            height: "2rem",
                        }}
                    />
                </Stack>
            </Box>

            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    marginTop: "1rem",
                    marginLeft: showSlider || showRealign ? "4rem" : "1rem",
                }}
            >
                <Stack direction="row" gap="1rem">
                    <MapLayerSelector
                        mapSelectedLayers={mapSelectedLayers}
                        setMapSelectedLayers={setMapSelectedLayers}
                        enableMaderaFarmUnitsLayer={enableMaderaFarmUnitsLayer}
                        availableRasterLayers={availableRasterLayers}
                    />
                    {showDateSelector && (
                        <ResultsDatePicker
                            date={date}
                            onDateChange={setDate}
                            sx={{minWidth: "14rem"}}
                            disabled={date === null}
                        />
                    )}
                </Stack>
            </Box>

            <Box
                sx={{
                    position: "absolute",
                    bottom: 0,
                    ...(alignLegend === "left" ? {left: 0, marginLeft: "1rem"} : {right: 0, marginRight: "1rem"}),
                    marginBottom: "2rem",
                }}
            >
                {layers.length > 0 && (
                    <MapLegend
                        layerType={mapSelectedLayers.layerType}
                        chosenLayerProps={chosenLayerProps}
                        min={layers[0]?.min}
                        max={layers[0]?.max}
                        dateStr={layers[0].date}
                    />
                )}
            </Box>

            {splitControl !== "none" && (
                <Box sx={{position: "absolute", top: 0, right: 0, marginTop: "1rem", marginRight: 0}}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onSplitTrigger}
                        startIcon={splitControl === "openSplit" ? <BackIcon /> : <CloseIcon />}
                        sx={{
                            ...(splitControl === "closeSplit" && {"& .MuiButton-startIcon": {margin: 0}}),
                            height: "2rem",
                            paddingX: "0.75rem",
                            minWidth: 0,
                            borderBottomRightRadius: 0,
                            borderTopRightRadius: 0,
                        }}
                    >
                        {splitControl === "openSplit" && t("map.split")}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
const getMapStyle = (mapLayers: MapSelectedLayers) => {
    if (mapLayers.satellite && mapLayers.streets) {
        return "mapbox://styles/mapbox/satellite-streets-v12";
    } else if (mapLayers.satellite) {
        return "mapbox://styles/mapbox/satellite-v9";
    } else if (mapLayers.streets) {
        return "mapbox://styles/mapbox/streets-v12";
    } else {
        return undefined;
    }
};

const getBorderLayerStyle = (ids: string[] | null): LineLayerSpecification => {
    return {
        id: "borders-layer",
        type: "line",
        source: "borders-source",
        paint: {
            "line-color": "#FAFCFF",
            "line-width": ["case", ["in", ["get", "id"], ["literal", ids]], 2, 1],
            "line-opacity": 1,
            "line-dasharray": ["case", ["in", ["get", "id"], ["literal", ids]], [1, 0], [2, 2]],
        },
    };
};

const MADERA_FILL_LAYER_STYLE: FillLayerSpecification = {
    id: "madera-fill-layer",
    source: "madera-fill-source",
    type: "fill",
    paint: {
        "fill-outline-color": "#000000",
        "fill-color": ["get", "color"],
        "fill-opacity": 0.5,
    },
};

const MADERA_LINE_LAYER_STYLE: LineLayerSpecification = {
    id: "madera-line-layer",
    source: "madera-line-source",
    type: "line",
    paint: {
        "line-color": ["get", "color"],
        "line-width": 2,
        "line-opacity": 1,
    },
};

// We need fill layer for the popup to work.
const FILL_LAYER_STYLE: FillLayerSpecification = {
    id: "popup-fill-layer",
    source: "popup-fill-source",
    type: "fill",
    paint: {
        "fill-color": "transparent",
        "fill-opacity": 0,
    },
};
